import { getBackendUrl } from './configService';
import type { UpdateVARInput } from '../types/var';
import type { ValidateLocationResult } from '../easymap/mapcontrols/VARMapTool/varLocation';

const backend = () => getBackendUrl() || '';

/**
 * Look up the region a coordinate falls in (or OUT_OF_BOUNDS) so the editor can
 * warn before save. Read-only advisory — the backend PUT still enforces.
 */
export async function validateVarLocation(lat: number, lon: number): Promise<ValidateLocationResult> {
  const res = await fetch(`${backend()}/api/var/validate-location?lat=${lat}&lon=${lon}`, {
    credentials: 'include',
  });
  if (!res.ok) throw new Error(`validate-location HTTP ${res.status}`);
  const body = await res.json();
  return { error: body.error, region: body.region };
}

/**
 * Delete a photo from CMS (var_photo node) via the backend's CMS proxy.
 * Returns true if the node was deleted (or already absent), false on error.
 */
export async function deleteCmsPhotoNode(uuid: string): Promise<boolean> {
  if (!uuid) return false;
  try {
    const res = await fetch(`${backend()}/api/cms/jsonapi/node/var_photo/${uuid}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Accept': 'application/vnd.api+json' },
    });
    return res.ok || res.status === 404;
  } catch (err) {
    console.error('[varPhotoApi] CMS photo delete failed', err);
    return false;
  }
}

/**
 * Delete a file entity from CMS. Must be called AFTER the referencing node is
 * deleted, otherwise Drupal returns 409 Conflict (file in use). Drupal does
 * not auto-delete orphaned files.
 */
export async function deleteCmsFile(fileUuid: string): Promise<boolean> {
  if (!fileUuid) return false;
  try {
    const res = await fetch(`${backend()}/api/cms/jsonapi/file/file/${fileUuid}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Accept': 'application/vnd.api+json' },
    });
    return res.ok || res.status === 404;
  } catch (err) {
    console.error('[varPhotoApi] CMS file delete failed', err);
    return false;
  }
}

/**
 * Delete var_photo node + its image file from CMS. Order matters: node first
 * (to release the file reference), then file (now orphaned, can be deleted).
 */
export async function deleteCmsPhotoAndFile(nodeUuid: string, fileUuid: string | null): Promise<boolean> {
  const nodeOk = await deleteCmsPhotoNode(nodeUuid);
  if (fileUuid) await deleteCmsFile(fileUuid);
  return nodeOk;
}

/**
 * Delete a photo from PG (var_points.photos array). Index must be the current
 * position in the array — caller should refresh after each delete since indices
 * shift.
 */
export async function deletePgPhotoIndex(varDbId: string, index: number): Promise<boolean> {
  const res = await fetch(`${backend()}/api/var/${varDbId}/photos/${index}`, {
    method: 'DELETE',
    credentials: 'include',
  });
  return res.ok;
}

/**
 * Convenience: delete from both CMS (node + file) and PG. CMS failure does
 * not block the PG delete (the source of truth for what's "active" on map).
 */
export async function deletePhotoEverywhere(
  varDbId: string,
  pgIndex: number,
  cmsNodeUuid: string,
  cmsFileUuid: string | null,
): Promise<{ cmsDeleted: boolean; pgDeleted: boolean }> {
  const [cmsDeleted, pgDeleted] = await Promise.all([
    deleteCmsPhotoAndFile(cmsNodeUuid, cmsFileUuid),
    deletePgPhotoIndex(varDbId, pgIndex),
  ]);
  return { cmsDeleted, pgDeleted };
}

export async function updateVar(varDbId: string, payload: UpdateVARInput): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${backend()}/api/var/${varDbId}`, {
      method: 'PUT',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.message || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Update failed' };
  }
}

export async function deleteVar(varDbId: string, reason: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${backend()}/api/var/${varDbId}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.message || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Delete failed' };
  }
}

export async function archiveVar(varDbId: string, reason: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${backend()}/api/var/${varDbId}/archive`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.message || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Archive failed' };
  }
}

export async function unarchiveVar(varDbId: string, reason: string): Promise<{ ok: boolean; error?: string }> {
  try {
    const res = await fetch(`${backend()}/api/var/${varDbId}/unarchive`, {
      method: 'PATCH',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      return { ok: false, error: body.message || `HTTP ${res.status}` };
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Unarchive failed' };
  }
}

/**
 * Set the `field_var_state` field on a var_photo CMS node to mirror the
 * parent VAR's lifecycle state. Called by VAREditModal on archive/unarchive
 * for each photo on the VAR. EM3 never touches Drupal's publish flag.
 */
export async function setCmsPhotoState(uuid: string, state: 'Active' | 'Archived'): Promise<boolean> {
  if (!uuid) return false;
  try {
    const res = await fetch(`${backend()}/api/cms/jsonapi/node/var_photo/${uuid}`, {
      method: 'PATCH',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        'Accept': 'application/vnd.api+json',
      },
      body: JSON.stringify({
        data: {
          type: 'node--var_photo',
          id: uuid,
          attributes: { field_var_state: state },
        },
      }),
    });
    return res.ok;
  } catch (err) {
    console.error('[varPhotoApi] setCmsPhotoState failed', err);
    return false;
  }
}
